import { Box, Typography, Grid, Paper, styled, Stack, Button, CircularProgress } from "@mui/material";
import DoneIcon from '@mui/icons-material/Done';
import jwtAxios from "../../utils/jwtAxios";
import "react-loading-skeleton/dist/skeleton.css";


export interface PricingTier {
    id: string;
    name: string;
    price: number;
}

interface SubscriptionTableProps {
    currentSubscription: string;
    pricingTiers: PricingTier[];
    dashboardRequired?: boolean;
    loading?: boolean;
}

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({ currentSubscription, pricingTiers, dashboardRequired = false, loading = false }) => {

    const monthlyTiers = pricingTiers.filter(tier => tier.name.includes("Monthly"));

    const tierMap: { [key: string]: { id: string; price: number, name: string } } = {
        "tier_starter": { ...(monthlyTiers.find(tier => tier.name.includes("Starter")) ?? { id: "", price: 0 }), name: "Starter",
        },
        "tier_professional": { ...(monthlyTiers.find(tier => tier.name.includes("Professional")) ?? { id: "", price: 0 }), name: "Professional",
        },
        "tier_enterprise": {...(monthlyTiers.find(tier => tier.name.includes("Enterprise")) ?? { id: "", price: 0 }), name: "Enterprise",
        }
    };

    const features = {
        "tier_starter": ["1 Beacon Group", "10 Beacons", "1 Group Share"],
        "tier_professional": ["5 Beacon Groups", "100 Beacons", "5 Group Shares", "Custom Domain"],
        "tier_enterprise": ["50 Beacon Groups", "1000 Beacons", "10 Group Shares", "Custom Domain", "Custom Themes", "API Integration with existing CMS"]
    };
    
    const renderTier = (tierKey: string, features: string[]) => {
        const tier = tierMap[tierKey];
        return (
            <Grid item xs={3} key={tierKey}>
                <Item sx={currentSubscription === tierKey ? {border: "solid 2px", borderImageSource: "linear-gradient(to bottom, rgba(255, 103, 31, 1) 50%, hsl(20, 100%, 50%) 70%)", borderImageSlice: 1}  : {}}>
                    <Typography variant="h5" mb={10} fontWeight={600}>{tier.name}</Typography>
                    <Box mb={6} borderBottom="solid">
                        <Stack alignItems="center" direction="row" gap={2} justifyContent="center" mb={10}>
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <Typography variant="h3">${getPrice(tier.price) ?? ""}</Typography>
                            )}
                            <Typography variant="body1">per month</Typography>
                        </Stack>
                    </Box>
                    <Box mb={8} minHeight={170} textAlign="left">
                        {features.map((feature, index) => (
                            <Stack key={index} alignItems="center" direction="row" gap={2} marginLeft={5}>
                                <DoneIcon sx={{ color: "green" }} />
                                <Typography variant="body1">{feature}</Typography>
                            </Stack>
                        ))}
                    </Box>
                    <Button
                        variant="contained"
                        sx={{ paddingX: 4, width: "90%" }}
                        onClick={() =>
                            currentSubscription === tierKey
                                ? undefined
                                : manageSubscription(tier.id, dashboardRequired ? "manage" : "subscribe")
                        }
                        disabled={currentSubscription === tierKey}
                    >
                        {currentSubscription === tierKey
                            ? "Your Current Subscription"
                            : dashboardRequired
                            ? "Change Subscription"
                            : "Choose Subscription"}
                    </Button>
                </Item>
            </Grid>
        );
    };    

    const manageSubscription = (tier: string, action: "manage" | "subscribe") => {
        const apiUrl = `api/subscription/${action}?productId=${tier}`;
        jwtAxios
            .post(apiUrl)
            .then(response => {
                window.location.href = response.data.url;
            })
            .catch(error => console.error("API request failed:", error));
    };
    

    const getPrice = (price: number) => {
        if (price > 0) {
            return price / 100; 
        } else {
            return null;
        }
        
    };

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        ...theme.typography.body1,
        padding: theme.spacing(3),
        textAlign: 'center',
      }));
      
      return (
        <Box sx={{width: '100%', background: "hsla(0, 0%, 97%, 1)", pb: 10, pt: 10}}>
            <Grid container rowSpacing={1} columnSpacing={{xs: 3, sm: 3, md: 6}} alignItems="center" justifyContent="center">
                {Object.entries(features).map(([key, featureList]) =>
                    renderTier(key, featureList)
                )}
            </Grid>
        </Box>
    );
};

export default SubscriptionTable;